import type { FC } from "react";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import clsx from "clsx";

import type { ICommonServiceOutput } from "@/Components/Api/CommonServiceOutput";
import { FetchGet } from "@/lib/fetch-helper";
import { sendTelemetryEvent } from "@/lib/telemetry";
import UserCommunicationModal from "@/src/common/UserCommunicationModal";
import {
    LoginModalView,
    languageSelector,
    useActiveLoginModalContext,
    useUserAuthenticationContext,
} from "@/src/contexts";
import { useMutationCustom, useQueryClientCustom } from "@/src/query-client-overrides";
import PageLayout from "@/src/shared/page-layout";
import { VoucherCampaignType } from "@/src/voucher/Voucher";
import TelemetryButton from "@Components/Button/TelemetryButton/TelemetryButton";
import ImageLoader from "@Components/ImageLoader";
import { Loading } from "@Components/Loading";
import {
    CommonEventType,
    MgmEventType,
    TelemetryEntity,
    getCdnImage,
    urlFormat,
} from "@Components/Modules/Helpers";
import { TextboxAppearanceV2, TextboxV2 } from "@Components/Textbox";
import { mgmServicesCodeKey } from "@Constants/PortalConstants";
import { CashbackDetails } from "@Routes/CashbackLinks";
import { VoucherPage } from "@Routes/CommonLinks";
import { Default, Index, LoginAndRegistrationPage, PhoneVerification } from "@Routes/MemberGetMemberLinks";

import { MgmRedeemCodePageResources } from "./MgmRedeemCodePage.Resources";
import { MgmRedeemCodePageResourcesEn } from "./MgmRedeemCodePage.Resources.En";

import * as portalCss from "../../shared/Portal.module.css";
import * as css from "./MgmRedeemCodePage.module.css";

type MgmRedeemFormValues = {
    [mgmServicesCodeKey]: string;
};

type ValidateMgmCodeOutput = {
    commonServiceOutput: ICommonServiceOutput;
    voucherCampaignType?: VoucherCampaignType;
    ownerId?: string;
};

export type IMgmRedeemCodePage = {
    code?: string;
    isMobile: boolean;
};

const MgmRedeemCodePage: FC<IMgmRedeemCodePage> = ({ code, isMobile }) => {
    const { setActiveLoginModal } = useActiveLoginModalContext();
    const resources = languageSelector(MgmRedeemCodePageResources, MgmRedeemCodePageResourcesEn);
    const { isAuthenticated } = useUserAuthenticationContext();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const queryClient = useQueryClientCustom();

    const buttonRef = useRef<HTMLButtonElement>();

    const {
        register,
        formState: { errors },
        handleSubmit,
        setError,
    } = useForm<MgmRedeemFormValues>({
        defaultValues: {
            redeemCode: code,
        },
        mode: "onChange",
    });

    const queryMutation = useMutationCustom({
        mutationFn: (values: MgmRedeemFormValues) =>
            FetchGet<ValidateMgmCodeOutput>(
                urlFormat(
                    Default,
                    {
                        action: "ValidateMgmCode",
                        code: values[mgmServicesCodeKey],
                    },
                    true,
                ),
            ),
        onSuccess: (response, variables) => {
            if (response?.commonServiceOutput?.resultStatus?.success !== true) {
                setError("redeemCode", {
                    type: "custom",
                    message: response.commonServiceOutput.resultStatus.errorMessage,
                });
                return;
            }

            if (response?.commonServiceOutput?.resultStatus?.success) {
                queryClient.removeQueries({
                    queryKey: [CashbackDetails, "cashbackDashboard"],
                });

                if (response.voucherCampaignType === VoucherCampaignType.MGM) {
                    if (isAuthenticated) {
                        navigate(urlFormat(PhoneVerification, { ownerId: response.ownerId }));
                    } else {
                        navigate(
                            urlFormat(LoginAndRegistrationPage, {
                                redeemCode: variables[mgmServicesCodeKey],
                            }),
                        );
                    }
                    return;
                }

                if (!isAuthenticated) {
                    setActiveLoginModal(location.hash === "#rejestracja"? LoginModalView.Registration : LoginModalView.Login);
                    return;
                }

                const navigateUrl = urlFormat(VoucherPage, { code: variables[mgmServicesCodeKey] });
                queryClient.removeQueries({
                    queryKey: [navigateUrl],
                });
                navigate(navigateUrl);
            }
        },
    });

    useEffect(() => {
        sendTelemetryEvent({
            eventName: `${TelemetryEntity.Common}-${MgmEventType.RedeemCodePage}`,
            actionType: "View",
        });

        if (code) {
            buttonRef.current.click();
        }
    }, []);

    return (
        <PageLayout
            isNewGrid
            breadcrumbs={isMobile && [{ labelKey: pathname.startsWith(Index) ? "mgm" : "code" }]}
        >
            <div
                className={`${css.mgmRedeemCodePageComponent} ${portalCss.standardContainerV2} ${portalCss.containerMargins}`}
            >
                <div className={clsx(css.redeemContainer, portalCss.setSectionColOne)}>
                    <p className={css.title} dangerouslySetInnerHTML={{ __html: resources.title }} />
                    <ImageLoader src={getCdnImage(process.env.MGM_MainImg)} alt="redeem mgm code logo" />

                    <div className={css.codeWrapper}>
                        <p
                            className={css.description}
                            dangerouslySetInnerHTML={{ __html: resources.description }}
                        />

                        <form onSubmit={handleSubmit((value) => queryMutation.mutate(value))}>
                            <TextboxV2
                                {...register(mgmServicesCodeKey, {
                                    required: resources.codeInputRequiredMessage,
                                    pattern: {
                                        value: /^[a-z0-9]+$/i,
                                        message: resources.codeInputRequiredMessage,
                                    },
                                })}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                                        e.currentTarget.form?.requestSubmit();
                                        e.preventDefault();
                                    }
                                }}
                                type="text"
                                appearance={TextboxAppearanceV2.Round}
                                placeholder={resources.codePlaceholder}
                                errorMessage={errors[mgmServicesCodeKey]?.message}
                                aria-label={resources.codePlaceholder}
                            />

                            {queryMutation.isPending ? (
                                <Loading />
                            ) : (
                                <TelemetryButton
                                    ref={buttonRef}
                                    appearance="magentaRoundAppearance"
                                    type="submit"
                                    telemetryCustom={{
                                        eventName: `${TelemetryEntity.Common}-${CommonEventType.next}`,
                                        actionType: "Click",
                                    }}
                                >
                                    {resources.nextButtonText}
                                </TelemetryButton>
                            )}
                        </form>
                    </div>
                </div>

                {!!queryMutation.data?.commonServiceOutput?.userCommunication && (
                    <UserCommunicationModal {...queryMutation.data.commonServiceOutput.userCommunication} />
                )}
            </div>
        </PageLayout>
    );
};

export { MgmRedeemCodePage };
